export const formatExactMoneyCents = (
  totalCents: number,
  withDollarSign: boolean = true
): string => {
  const dollars = Math.floor(totalCents / 100).toString();
  const cents = Math.floor(totalCents % 100);
  const startingCharacter = withDollarSign ? "$" : "";

  const parts = [];
  for (let i = dollars.length; i > 0; i -= 3) {
    parts.unshift(dollars.substring(Math.max(0, i - 3), i));
  }

  if (cents == 0) {
    return `${startingCharacter}${parts.join(",")}`;
  }
  return `${startingCharacter}${parts.join(",")}.${cents}`;
};

export const formatExactMoneyDollars = (
  dollars: number,
  options: { withRounding: boolean; withDollarSign: boolean } = {
    withRounding: false,
    withDollarSign: true,
  }
): string => {
  if (options.withRounding) {
    dollars = Math.round(dollars);
  }
  return formatExactMoneyCents(dollars * 100, options.withDollarSign);
};

// Not really significant digits in the classical sense, but rather the maximum
// number of non-zero digits we ideally show for a given value.
const SIGNIFICANT_DIGITS = 3;

// At what `threshold` should we add `unit` and remove `dropDigits` off the right.
const DIGIT_LENGTH_TO_HANDLING = [
  {
    threshold: 10,
    unit: "B",
    dropDigits: 9,
  },
  {
    threshold: 7,
    unit: "MM",
    dropDigits: 6,
  },
  {
    threshold: 4,
    unit: "K",
    dropDigits: 3,
  },
] as const;

const parse = (x: string): number => {
  return x === "" ? 0 : parseInt(x, 10);
};

export const formatAbbreviatedMoney = (
  dollars: number,
  withDollarSign: boolean = true
): string => {
  // XXX being defensive against fractional values passed in during demo due to random(), etc.
  dollars = Math.round(dollars);

  for (const { threshold, unit, dropDigits } of DIGIT_LENGTH_TO_HANDLING) {
    const shift = Math.min(
      dollars.toString().length - SIGNIFICANT_DIGITS,
      dropDigits
    );
    const roundedDollars =
      dollars.toString().length > 3
        ? Math.round(dollars / Math.pow(10, shift)) * Math.pow(10, shift)
        : dollars;
    const length = roundedDollars.toString().length;

    if (length >= threshold) {
      const significand = roundedDollars.toString();
      const dotPlace = length - dropDigits;

      const whole = significand.substring(0, dotPlace);
      let partial = significand.substring(
        dotPlace,
        Math.max(SIGNIFICANT_DIGITS, length - dropDigits)
      );

      // Note this may drop some "significant digits" in the sake of cleaner output in the UI.
      while (partial.endsWith("0")) {
        partial = partial.substring(0, partial.length - 1);
      }

      const formattedWhole = formatExactMoneyDollars(parse(whole), {
        withDollarSign,
        withRounding: false,
      });
      const formattedPartial = partial ? "." + partial : "";
      return `${formattedWhole}${formattedPartial}${unit}`;
    }
  }
  return formatExactMoneyDollars(dollars, {
    withDollarSign: withDollarSign,
    withRounding: false,
  });
};

/** Like {@link formatAbbreviatedMoney}, but never rounds the significand: every
 *  digit the value has is shown, with only trailing zeros trimmed. So 5,625,000
 *  renders as `$5.625MM` (not `$5.63MM`) while round values stay short (`$5MM`).
 *  Use where an abbreviated value must read as exact rather than approximate.
 *  Pass `maxDecimals` to cap the fractional part to that many places of the
 *  unit: with `maxDecimals = 3`, 1,234,567 is `$1.235MM` not `$1.234567MM`. */
export const formatAbbreviatedMoneyExact = (
  dollars: number,
  withDollarSign: boolean = true,
  maxDecimals?: number
): string => {
  // XXX being defensive against fractional values passed in during demo due to random(), etc.
  dollars = Math.round(dollars);

  const length = dollars.toString().length;

  for (const { threshold, unit, dropDigits } of DIGIT_LENGTH_TO_HANDLING) {
    if (length >= threshold) {
      // Round to 10^(dropDigits - maxDecimals) to cap decimals; step 1 = exact.
      const step =
        maxDecimals === undefined ? 1 : Math.pow(10, dropDigits - maxDecimals);
      const value = Math.round(dollars / step) * step;

      const significand = value.toString();
      const dotPlace = significand.length - dropDigits;
      const whole = significand.substring(0, dotPlace);

      let partial = significand.substring(dotPlace);
      while (partial.endsWith("0")) {
        partial = partial.substring(0, partial.length - 1);
      }

      const formattedWhole = formatExactMoneyDollars(parse(whole), {
        withDollarSign,
        withRounding: false,
      });
      const formattedPartial = partial ? "." + partial : "";
      return `${formattedWhole}${formattedPartial}${unit}`;
    }
  }
  return formatExactMoneyDollars(dollars, {
    withDollarSign,
    withRounding: false,
  });
};

export const AbbreviatedMoney = ({ dollars }: { dollars: number }) => {
  return <>{formatAbbreviatedMoney(dollars)}</>;
};

export const ExactMoney = ({ cents }: { cents: number }) => {
  return <>{formatExactMoneyCents(cents)}</>;
};

export const LayerPremiumSummary: React.FC<{
  layerTotal: number;
  shareTotal: number;
}> = ({ layerTotal, shareTotal }) => (
  <div
    style={{
      fontSize: "0.85rem",
      color: "var(--text-color-secondary)",
    }}
  >
    Layer premium:{" "}
    {formatExactMoneyDollars(layerTotal, {
      withRounding: true,
      withDollarSign: true,
    })}
    {" · "}Share premium:{" "}
    {formatExactMoneyDollars(shareTotal, {
      withRounding: true,
      withDollarSign: true,
    })}
  </div>
);
